import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiCreditCard,
    FiShield,
    FiTruck,
    FiPlusCircle,
    FiTrash2,
    FiUser
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import studentService from "../../services/studentService";
import feeStructureService from "../../services/feeStructureService";
import fineService from "../../services/fineService";
import transportService from "../../services/transportService";

const todayDMY = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const INSTALLMENT_LABELS = [
    "First Installment", "Second Installment", "Third Installment",
    "Fourth Installment", "Fifth Installment", "Full Payment",
];

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const EMPTY_FORM = {
    installmentNo: "1",
    installmentLabel: "First Installment",
    amountPaid: "",
    paymentDate: todayDMY(),
    paymentMode: "Cash",
    chequeNo: "",
    bankName: "",
    transactionRef: "",
    remarks: "",
};

export default function FeeReceiptForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { academicYear, role, selectedCollegeId } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    // Student selection
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentSearch, setStudentSearch] = useState("");
    const [students, setStudents] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(false);

    // Student fee summary
    const [feeSummary, setFeeSummary] = useState(null);

    // Next receipt number preview
    const [nextReceiptNo, setNextReceiptNo] = useState("");

    // Fee Structure breakdown for the selected student
    const [structurePreview, setStructurePreview] = useState(null);
    const [headAmounts, setHeadAmounts] = useState({});
    const [loadingStructure, setLoadingStructure] = useState(false);
    const [useManualAmount, setUseManualAmount] = useState(false);

    // Pending fines for student
    const [pendingFines, setPendingFines] = useState([]);
    const [selectedFineIds, setSelectedFineIds] = useState([]);
    const [fineAmounts, setFineAmounts] = useState({});

    // Transport for student
    const [studentTransport, setStudentTransport] = useState(null);
    const [includeTransport, setIncludeTransport] = useState(false);
    const [transportMonths, setTransportMonths] = useState(1);
    const [transportFeeAmount, setTransportFeeAmount] = useState(0);

    // Custom fee heads added on the fly
    const [customHeads, setCustomHeads] = useState([]);

    const hasStructure = Boolean(structurePreview?.structure) && !useManualAmount;
    
    // Structure breakdown subtotal
    const structureSubtotal = useMemo(() => {
        if (!hasStructure) return Number(form.amountPaid) || 0;
        return Object.values(headAmounts).reduce((sum, v) => sum + (Number(v) || 0), 0);
    }, [hasStructure, headAmounts, form.amountPaid]);

    // Fines subtotal
    const finesSubtotal = useMemo(() => {
        return selectedFineIds.reduce((sum, fId) => sum + (Number(fineAmounts[fId]) || 0), 0);
    }, [selectedFineIds, fineAmounts]);

    // Transport subtotal
    const transportSubtotal = useMemo(() => {
        return includeTransport ? Number(transportFeeAmount) || 0 : 0;
    }, [includeTransport, transportFeeAmount]);

    // Custom heads subtotal
    const customHeadsSubtotal = useMemo(() => {
        return customHeads.reduce((sum, h) => sum + (Number(h.amount) || 0), 0);
    }, [customHeads]);

    // Grand total
    const grandTotal = useMemo(() => {
        return structureSubtotal + finesSubtotal + transportSubtotal + customHeadsSubtotal;
    }, [structureSubtotal, finesSubtotal, transportSubtotal, customHeadsSubtotal]);

    const studentDetailPath = selectedStudent?._id ? `${feeBasePath}/student/${selectedStudent._id}` : `${feeBasePath}/receipts`;

    // ── Load for edit ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await feeService.getById(id);
                const r = res.data;
                setSelectedStudent(r.student);
                setStudentSearch(r.student?.fullName || "");
                setForm({
                    installmentNo: String(r.installmentNo),
                    installmentLabel: r.installmentLabel,
                    amountPaid: String(r.amountPaid),
                    paymentDate: r.paymentDate,
                    paymentMode: r.paymentMode,
                    chequeNo: r.chequeNo || "",
                    bankName: r.bankName || "",
                    transactionRef: r.transactionRef || "",
                    remarks: r.remarks || "",
                });
                if (!r.breakdown || r.breakdown.length === 0) {
                    setUseManualAmount(true);
                } else {
                    loadStructurePreview(r.student?._id, r.breakdown);
                }
                loadFeeSummary(r.student?._id);
            } catch (err) {
                toast.error(err.message);
                navigate(`${feeBasePath}/receipts`);
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, navigate, feeBasePath]);

    // ── Next receipt number ───────────────────────────────────────────────────
    useEffect(() => {
        if (isEdit || !academicYear?._id) return;
        feeService.getNextReceiptNo(academicYear._id)
            .then((r) => setNextReceiptNo(r.data?.receiptNo || r.receiptNo || ""))
            .catch(() => { });
    }, [isEdit, academicYear, selectedCollegeId]);

    // ── Student search ────────────────────────────────────────────────────────
    useEffect(() => {
        if (isEdit) return;
        const t = setTimeout(async () => {
            if (!studentSearch.trim()) { setStudents([]); return; }
            setLoadingStudents(true);
            try {
                const res = await studentService.getAll({
                    search: studentSearch.trim(),
                    status: "Active",
                    limit: 12,
                });
                const list = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.students)
                        ? res.data.students
                        : (Array.isArray(res.data?.data) ? res.data.data : []);
                setStudents(list);
            } catch { setStudents([]); }
            finally { setLoadingStudents(false); }
        }, 250);
        return () => clearTimeout(t);
    }, [studentSearch, isEdit, selectedCollegeId]);

    const loadFeeSummary = async (studentId) => {
        try {
            const res = await feeService.getStudentSummary(studentId);
            setFeeSummary(res.data || res);
        } catch { setFeeSummary(null); }
    };

    const loadStructurePreview = async (studentId, prefillFromBreakdown = []) => {
        setLoadingStructure(true);
        try {
            const res = await feeStructureService.previewForStudent(studentId);
            const data = res.data || res;
            setStructurePreview(data);
            const seeded = {};
            (data.breakdown || []).forEach((b) => {
                const existing = prefillFromBreakdown.find((p) => p.head === b.head);
                seeded[b.head] = existing ? String(existing.amount) : "0";
            });
            setHeadAmounts(seeded);
        } catch (err) {
            setStructurePreview(null);
            setHeadAmounts({});
        } finally {
            setLoadingStructure(false);
        }
    };

    const selectStudent = async (s) => {
        setSelectedStudent(s);
        setStudents([]);
        setStudentSearch(s.fullName);
        setUseManualAmount(false);
        loadFeeSummary(s._id);
        loadStructurePreview(s._id);

        // Load student fines
        try {
            const fRes = await fineService.getAll({ student: s._id, status: "Pending" });
            const fList = Array.isArray(fRes.data)
                ? fRes.data
                : (Array.isArray(fRes.data?.data) ? fRes.data.data : Array.isArray(fRes) ? fRes : []);
            setPendingFines(fList);
            const fMap = {};
            fList.forEach((f) => { fMap[f._id] = f.amount; });
            setFineAmounts(fMap);
            setSelectedFineIds([]);
        } catch (e) {
            setPendingFines([]);
            setSelectedFineIds([]);
        }

        // Load student bus transport
        try {
            const tRes = await transportService.getStudentHistory(s._id);
            const tList = Array.isArray(tRes.data)
                ? tRes.data
                : (Array.isArray(tRes.data?.data) ? tRes.data.data : Array.isArray(tRes) ? tRes : []);
            const activeTransport = tList.find((t) => t.status === "Active") || null;
            setStudentTransport(activeTransport);
            setIncludeTransport(false);
            setTransportMonths(1);
            setTransportFeeAmount(activeTransport ? activeTransport.monthlyFee : 0);
        } catch (e) {
            setStudentTransport(null);
            setIncludeTransport(false);
        }

        setCustomHeads([]);
    };

    const clearStudent = () => {
        setSelectedStudent(null);
        setStudentSearch("");
        setFeeSummary(null);
        setStructurePreview(null);
        setHeadAmounts({});
        setPendingFines([]);
        setSelectedFineIds([]);
        setStudentTransport(null);
        setIncludeTransport(false);
        setCustomHeads([]);
    };

    // Fine toggle
    const toggleFine = (fineId) => {
        setSelectedFineIds((prev) => {
            if (prev.includes(fineId)) {
                return prev.filter((id) => id !== fineId);
            } else {
                return [...prev, fineId];
            }
        });
    };

    // Fine amount edit
    const handleFineAmountChange = (fineId, val) => {
        setFineAmounts((prev) => ({ ...prev, [fineId]: Number(val) || 0 }));
    };

    // Transport months change
    const handleTransportMonthsChange = (months) => {
        const m = Math.max(1, Number(months) || 1);
        setTransportMonths(m);
        if (studentTransport) {
            setTransportFeeAmount(m * studentTransport.monthlyFee);
        }
    };

    // Custom heads management
    const addCustomHead = () => {
        setCustomHeads((prev) => [
            ...prev,
            { id: Date.now(), head: "", amount: "", category: "Other" }
        ]);
    };

    const updateCustomHead = (id, field, value) => {
        setCustomHeads((prev) =>
            prev.map((h) => (h.id === id ? { ...h, [field]: value } : h))
        );
    };

    const removeCustomHead = (id) => {
        setCustomHeads((prev) => prev.filter((h) => h.id !== id));
    };

    const handleHeadAmountChange = (headName, value) => {
        setHeadAmounts((prev) => ({ ...prev, [headName]: value }));
    };

    // ── Form handlers ─────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => {
            const next = { ...p, [name]: value };
            if (name === "installmentNo") {
                const n = parseInt(value, 10);
                next.installmentLabel = INSTALLMENT_LABELS[n - 1] || `Installment ${n}`;
            }
            return next;
        });
        if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    };

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!selectedStudent) errs.student = "Select a student";
        if (grandTotal <= 0) {
            errs.amountPaid = "Total payment must be greater than ₹0";
        }
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(form.paymentDate)) errs.paymentDate = "Format: DD/MM/YYYY";
        if (!form.installmentNo || isNaN(parseInt(form.installmentNo, 10)))
            errs.installmentNo = "Required";
        if ((form.paymentMode === "Cheque" || form.paymentMode === "DD") && !form.chequeNo.trim())
            errs.chequeNo = "Cheque/DD number required";
        if ((form.paymentMode === "NEFT" || form.paymentMode === "UPI") && !form.transactionRef.trim())
            errs.transactionRef = "Transaction reference required";
        return errs;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }
        if (!isEdit && !academicYear?._id) {
            toast.error("Select an Academic Year first.");
            return;
        }

        setLoading(true);
        try {
            // 1. Structure heads
            const breakdown = [];
            if (hasStructure) {
                Object.entries(headAmounts)
                    .filter(([, amount]) => Number(amount) > 0)
                    .forEach(([head, amount]) => {
                        breakdown.push({ head, amount: Number(amount), category: "Academic" });
                    });
            } else if (Number(form.amountPaid) > 0) {
                breakdown.push({ head: "Tuition / Academic Fee", amount: Number(form.amountPaid), category: "Academic" });
            }

            // 2. Selected fines & charges
            selectedFineIds.forEach((fId) => {
                const fineDoc = pendingFines.find((f) => f._id === fId);
                const fineAmt = Number(fineAmounts[fId]) || fineDoc?.amount || 0;
                if (fineDoc && fineAmt > 0) {
                    breakdown.push({
                        head: `${fineDoc.category === "Charge" ? "Charge" : "Fine"}: ${fineDoc.title}`,
                        category: fineDoc.category === "Charge" ? "Other" : "Fine",
                        amount: fineAmt,
                        fineId: fineDoc._id,
                    });
                }
            });

            // 3. Transport fee
            if (includeTransport && transportFeeAmount > 0) {
                breakdown.push({
                    head: `Bus / Transport Fee (${transportMonths} mo · ${studentTransport?.route?.routeName || "Bus Pass"})`,
                    category: "Transport",
                    amount: Number(transportFeeAmount),
                });
            }

            // 4. Custom fee heads
            customHeads.forEach((h) => {
                if (h.head.trim() && Number(h.amount) > 0) {
                    breakdown.push({
                        head: h.head.trim(),
                        category: h.category || "Other",
                        amount: Number(h.amount),
                    });
                }
            });

            const payload = {
                student: selectedStudent._id,
                academicYear: academicYear?._id || selectedStudent?.academicYear?._id || selectedStudent?.academicYear,
                installmentNo: parseInt(form.installmentNo, 10),
                installmentLabel: form.installmentLabel,
                amountPaid: grandTotal,
                breakdown,
                paidFineIds: selectedFineIds,
                paymentDate: form.paymentDate,
                paymentMode: form.paymentMode,
                chequeNo: form.chequeNo,
                bankName: form.bankName,
                transactionRef: form.transactionRef,
                remarks: form.remarks,
            };

            if (isEdit) {
                await feeService.update(id, payload);
                toast.success("Receipt updated");
            } else {
                const res = await feeService.create(payload);
                toast.success(`Receipt ${res.data?.receiptNo || res.receiptNo || ""} created successfully`);
            }
            navigate(studentDetailPath);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to record payment");
        } finally {
            setLoading(false);
        }
    };

    const needsCheque = form.paymentMode === "Cheque" || form.paymentMode === "DD";
    const needsRef = form.paymentMode === "NEFT" || form.paymentMode === "UPI";

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading receipt…</div>;

    return (
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
            {/* ── Page Header ── */}
            <div className="page-header" style={{ marginBottom: "1rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Fee Receipt" : "Collect Student Fees & Fines"} 💳</h1>
                    <p className="page-subtitle">
                        {isEdit
                            ? "Update existing fee payment voucher"
                            : "Record academic fees, bus transport passes, fines, or other charges in a single receipt"
                        }
                    </p>
                </div>
                {!isEdit && nextReceiptNo && (
                    <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", padding: "6px 14px", borderRadius: 8, fontSize: 13, color: "#002747", fontWeight: 700 }}>
                        Next Receipt: {nextReceiptNo}
                    </div>
                )}
            </div>

            {!isEdit && !academicYear && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#dc2626" }}>
                    ⚠️ No academic year selected. Go to Dashboard first.
                </div>
            )}

            <form onSubmit={handleSubmit}>

                {/* ── 1. Student Selection ────────────────────────────────────────── */}
                <div className="card" style={{ marginBottom: "1.25rem" }}>
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiUser /> 1. Select Student
                    </div>
                    <div className="card-body">
                        {!isEdit && (
                            <>
                                <div className="student-select-box">
                                    <div className="student-select-search">
                                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "#9ca3af" }}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <input
                                            placeholder="Type student name or enrollment / registration no…"
                                            value={studentSearch}
                                            onChange={(e) => { setStudentSearch(e.target.value); if (selectedStudent) clearStudent(); }}
                                        />
                                        {loadingStudents && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Searching…</span>}
                                    </div>
                                    {students.length > 0 && (
                                        <div className="student-select-list">
                                            {students.map((s) => (
                                                <div key={s._id}
                                                    className={`student-select-item ${selectedStudent?._id === s._id ? "selected" : ""}`}
                                                    onClick={() => selectStudent(s)}>
                                                    <div className="student-name">{s.fullName}</div>
                                                    <div className="student-meta">
                                                        {s.enrollmentNo ? `Reg: ${s.enrollmentNo} · ` : ""}
                                                        {s.courseAdmitted || s.course?.courseName || ""} Yr {s.courseYear || s.yearNumber || "1"}
                                                        {" · Fees: ₹"}{s.feesDecided?.toLocaleString("en-IN")}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {errors.student && <span className="form-error">{errors.student}</span>}
                            </>
                        )}

                        {/* Selected student info + fee summary */}
                        {selectedStudent && (
                            <div style={{ marginTop: isEdit ? 0 : "0.75rem", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.75rem" }}>
                                <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1e40af" }}>{selectedStudent.fullName}</div>
                                    <div style={{ color: "var(--text-muted)", marginTop: 2 }}>
                                        {selectedStudent.enrollmentNo ? `Reg: ${selectedStudent.enrollmentNo}` : ""}
                                        {selectedStudent.courseAdmitted && ` · ${selectedStudent.courseAdmitted} ${selectedStudent.courseYear || ""}`}
                                    </div>
                                    {selectedStudent.mobile && <div style={{ fontSize: 11.5, color: "#64748b" }}>Mob: {selectedStudent.mobile}</div>}
                                </div>

                                {feeSummary && (
                                    <div style={{ background: feeSummary.pendingBalance > 0 ? "#fef9c3" : "#f0fdf4", border: `1px solid ${feeSummary.pendingBalance > 0 ? "#fde047" : "#86efac"}`, borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span style={{ color: "var(--text-muted)" }}>Tuition Decided:</span>
                                            <span style={{ fontWeight: 600 }}>{inr(feeSummary.feesDecided)}</span>
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                                            <span style={{ color: "var(--text-muted)" }}>Tuition Paid:</span>
                                            <span style={{ fontWeight: 600, color: "#16a34a" }}>{inr(feeSummary.totalPaid)}</span>
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                                            <span style={{ color: "var(--text-muted)" }}>Tuition Pending:</span>
                                            <span style={{ fontWeight: 700, color: feeSummary.pendingBalance > 0 ? "#dc2626" : "#16a34a" }}>
                                                {feeSummary.pendingBalance > 0 ? inr(feeSummary.pendingBalance) : "✓ Cleared"}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── 2. Fee Heads, Fines & Transport Breakdown ─────────────────────── */}
                {selectedStudent && (
                    <div className="card" style={{ marginBottom: "1.25rem" }}>
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                <FiCreditCard /> 2. Fee Heads, Fines & Charges Breakdown
                            </div>
                            {hasStructure && (
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    onClick={() => setUseManualAmount(true)}
                                >
                                    Enter tuition manually
                                </button>
                            )}
                            {useManualAmount && structurePreview?.structure && (
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    onClick={() => setUseManualAmount(false)}
                                >
                                    Use Fee Structure
                                </button>
                            )}
                        </div>

                        <div className="card-body" style={{ display: "grid", gap: "1.25rem" }}>
                            {/* A. Academic / Structure Heads */}
                            <div>
                                <h4 style={{ margin: "0 0 0.5rem 0", fontSize: "13.5px", fontWeight: 700, color: "var(--text)" }}>
                                    🎓 Academic & Tuition Heads
                                </h4>
                                {hasStructure && structurePreview?.breakdown ? (
                                    <div style={{ display: "grid", gap: "0.5rem", background: "#f8fafc", padding: "0.85rem", borderRadius: "8px", border: "1px solid var(--border)" }}>
                                        {structurePreview.breakdown.map((b) => (
                                            <div
                                                key={b.head}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 140px",
                                                    gap: "0.75rem",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{b.head}</div>
                                                    <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                        Structure Ref: {inr(b.amount)}
                                                    </div>
                                                </div>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    placeholder="0"
                                                    value={headAmounts[b.head] === "0" ? "" : (headAmounts[b.head] ?? "")}
                                                    onChange={(e) => handleHeadAmountChange(b.head, e.target.value || "0")}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: "1rem", alignItems: "center" }}>
                                        <div>
                                            <label className="form-label" style={{ margin: 0 }}>Tuition / Academic Amount (₹)</label>
                                            <div style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>Direct lump-sum academic fee collection</div>
                                        </div>
                                        <input
                                            type="number"
                                            min="0"
                                            name="amountPaid"
                                            value={form.amountPaid}
                                            onChange={handleChange}
                                            placeholder="0"
                                            className="form-control"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* B. Pending Institutional Charges & Fines */}
                            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                    <h4 style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: "#dc2626", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                        <FiShield /> Pending Institutional Charges &amp; Fines ({pendingFines.length})
                                    </h4>
                                    {pendingFines.length === 0 && (
                                        <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>✓ No pending charges or fines</span>
                                    )}
                                </div>

                                {pendingFines.length > 0 && (
                                    <div style={{ display: "grid", gap: "0.4rem", background: "#fef2f2", padding: "0.85rem", borderRadius: "8px", border: "1px solid #fecaca" }}>
                                        <p style={{ fontSize: "11.5px", color: "#991b1b", margin: "0 0 0.4rem 0" }}>
                                            Check the box to collect institutional charges (Uniform, Lab Manual, Books) or fines along with this receipt. Selected items will be marked as <strong>Paid</strong> upon submission without impacting the student's tuition balance.
                                        </p>
                                        {pendingFines.map((f) => {
                                            const isSelected = selectedFineIds.includes(f._id);
                                            return (
                                                <div
                                                    key={f._id}
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "auto 1fr 140px",
                                                        gap: "0.75rem",
                                                        alignItems: "center",
                                                        background: isSelected ? "#fee2e2" : "#fff",
                                                        padding: "6px 10px",
                                                        borderRadius: "6px",
                                                        border: isSelected ? "1px solid #f87171" : "1px solid #fed7d7",
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleFine(f._id)}
                                                        style={{ width: 16, height: 16, cursor: "pointer" }}
                                                    />
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                            <span style={{
                                                                fontSize: "10px",
                                                                fontWeight: 700,
                                                                padding: "1px 5px",
                                                                borderRadius: "4px",
                                                                background: f.category === "Charge" ? "#e8f1f8" : "#fef2f2",
                                                                color: f.category === "Charge" ? "#002747" : "#b91c1c",
                                                                border: `1px solid ${f.category === "Charge" ? "#b6d5ec" : "#fecaca"}`,
                                                            }}>
                                                                {f.category === "Charge" ? "👔 CHARGE" : "⚖ FINE"}
                                                            </span>
                                                            <strong style={{ fontSize: 13, color: "var(--text)" }}>
                                                                {f.title}
                                                            </strong>
                                                        </div>
                                                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: "2px" }}>
                                                            Type: {f.fineType} {f.dueDate ? `· Due: ${new Date(f.dueDate).toLocaleDateString("en-IN")}` : ""} · Decided: ₹{f.amount}
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        disabled={!isSelected}
                                                        className="form-control"
                                                        value={fineAmounts[f._id] ?? f.amount}
                                                        onChange={(e) => handleFineAmountChange(f._id, e.target.value)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* C. Bus / Transport Facility */}
                            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                    <h4 style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: "#003b67", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                        <FiTruck /> Bus / Transport Subscription
                                    </h4>
                                    {!studentTransport && (
                                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Not subscribed to college bus</span>
                                    )}
                                </div>

                                {studentTransport ? (
                                    <div style={{ background: "#e8f1f8", padding: "0.85rem", borderRadius: "8px", border: "1px solid #b6d5ec" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: 13, color: "#1e40af" }}>
                                                    Route: {studentTransport.route?.routeName || "Campus Route"} ({studentTransport.route?.busNumber ? `Bus: ${studentTransport.route.busNumber}` : "Bus"})
                                                </div>
                                                <div style={{ fontSize: 11.5, color: "#3b82f6" }}>
                                                    Stop: {studentTransport.stopName} · Distance: {studentTransport.distanceKm} km · Monthly Rate: ₹{studentTransport.monthlyFee}/mo
                                                </div>
                                            </div>
                                            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={includeTransport}
                                                    onChange={(e) => setIncludeTransport(e.target.checked)}
                                                    style={{ width: 16, height: 16 }}
                                                />
                                                Collect Bus Fee
                                            </label>
                                        </div>

                                        {includeTransport && (
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 140px", gap: "0.75rem", alignItems: "center", borderTop: "1px dashed #b6d5ec", paddingTop: "0.6rem" }}>
                                                <span style={{ fontSize: 12, fontWeight: 600 }}>Number of Months:</span>
                                                <select
                                                    className="form-control"
                                                    value={transportMonths}
                                                    onChange={(e) => handleTransportMonthsChange(e.target.value)}
                                                >
                                                    {[1, 2, 3, 4, 5, 6, 10, 12].map((m) => (
                                                        <option key={m} value={m}>{m} Month{m > 1 ? "s" : ""}</option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    value={transportFeeAmount}
                                                    onChange={(e) => setTransportFeeAmount(Number(e.target.value) || 0)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                        To assign a bus pass, use <strong>Bus / Transport</strong> from the menu.
                                    </div>
                                )}
                            </div>

                            {/* D. Custom Additional Heads */}
                            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                    <h4 style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: "var(--text)" }}>
                                        ➕ Additional Fee Heads (Uniform, Lab Manual, Clearance, etc.)
                                    </h4>
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        onClick={addCustomHead}
                                        style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
                                    >
                                        <FiPlusCircle size={13} /> Add Fee Head
                                    </button>
                                </div>

                                {customHeads.length > 0 && (
                                    <div style={{ display: "grid", gap: "0.5rem" }}>
                                        {customHeads.map((h) => (
                                            <div
                                                key={h.id}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 120px 140px 36px",
                                                    gap: "0.5rem",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <input
                                                    type="text"
                                                    placeholder="Fee Head Name (e.g. Uniform Fee, Lab Manual)"
                                                    className="form-control"
                                                    value={h.head}
                                                    onChange={(e) => updateCustomHead(h.id, "head", e.target.value)}
                                                />
                                                <select
                                                    className="form-control"
                                                    value={h.category}
                                                    onChange={(e) => updateCustomHead(h.id, "category", e.target.value)}
                                                >
                                                    <option value="Academic">Academic</option>
                                                    <option value="Fine">Fine</option>
                                                    <option value="Transport">Transport</option>
                                                    <option value="Hostel">Hostel</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="Amount (₹)"
                                                    className="form-control"
                                                    value={h.amount}
                                                    onChange={(e) => updateCustomHead(h.id, "amount", e.target.value)}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => removeCustomHead(h.id)}
                                                    style={{ padding: "6px" }}
                                                >
                                                    <FiTrash2 size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Summary Grand Total Banner */}
                            <div style={{
                                background: "#f0fdf4",
                                border: "1.5px solid #86efac",
                                borderRadius: "8px",
                                padding: "0.85rem 1.25rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}>
                                <div>
                                    <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#166534" }}>
                                        Receipt Grand Total
                                    </span>
                                    <div style={{ fontSize: 11, color: "#15803d" }}>
                                        Tuition: ₹{structureSubtotal} · Fines: ₹{finesSubtotal} · Bus: ₹{transportSubtotal} {customHeadsSubtotal > 0 ? `· Other: ₹${customHeadsSubtotal}` : ""}
                                    </div>
                                </div>
                                <div style={{ fontSize: 24, fontWeight: 900, color: "#15803d" }}>
                                    {inr(grandTotal)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── 3. Payment Voucher Details ──────────────────────────────────── */}
                <div className="card" style={{ marginBottom: "1.25rem" }}>
                    <div className="card-header">3. Payment & Voucher Details</div>
                    <div className="card-body">
                        <div className="form-grid">

                            {/* Installment No */}
                            <div className="form-group">
                                <label className="form-label">Installment No. <span style={{ color: "var(--danger)" }}>*</span></label>
                                <input type="number" name="installmentNo" value={form.installmentNo}
                                    onChange={handleChange} min="1" max="10"
                                    className={`form-control ${errors.installmentNo ? "error" : ""}`} />
                                {errors.installmentNo && <span className="form-error">{errors.installmentNo}</span>}
                            </div>

                            {/* Installment Label */}
                            <div className="form-group">
                                <label className="form-label">Installment Label <span style={{ color: "var(--danger)" }}>*</span></label>
                                <select name="installmentLabel" value={form.installmentLabel}
                                    onChange={handleChange} className="form-control">
                                    {INSTALLMENT_LABELS.map((l) => (
                                        <option key={l} value={l}>{l}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Payment Date */}
                            <div className="form-group">
                                <label className="form-label">Payment Date <span style={{ color: "var(--danger)" }}>*</span></label>
                                <input name="paymentDate" value={form.paymentDate}
                                    onChange={handleChange} placeholder="DD/MM/YYYY"
                                    className={`form-control ${errors.paymentDate ? "error" : ""}`} />
                                {errors.paymentDate && <span className="form-error">{errors.paymentDate}</span>}
                            </div>

                            {/* Payment Mode */}
                            <div className="form-group">
                                <label className="form-label">Payment Mode <span style={{ color: "var(--danger)" }}>*</span></label>
                                <select name="paymentMode" value={form.paymentMode}
                                    onChange={handleChange} className="form-control">
                                    {["Cash", "UPI", "Cheque", "NEFT", "DD"].map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Cheque/DD fields */}
                            {needsCheque && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">{form.paymentMode} Number <span style={{ color: "var(--danger)" }}>*</span></label>
                                        <input name="chequeNo" value={form.chequeNo} onChange={handleChange}
                                            placeholder={`Enter ${form.paymentMode} number`}
                                            className={`form-control ${errors.chequeNo ? "error" : ""}`} />
                                        {errors.chequeNo && <span className="form-error">{errors.chequeNo}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Bank Name</label>
                                        <input name="bankName" value={form.bankName} onChange={handleChange}
                                            placeholder="e.g. State Bank of India" className="form-control" />
                                    </div>
                                </>
                            )}

                            {/* NEFT/UPI ref */}
                            {needsRef && (
                                <div className="form-group">
                                    <label className="form-label">Transaction Reference (UTR / UPI ID) <span style={{ color: "var(--danger)" }}>*</span></label>
                                    <input name="transactionRef" value={form.transactionRef} onChange={handleChange}
                                        placeholder="e.g. UPI / UTR Reference ID"
                                        className={`form-control ${errors.transactionRef ? "error" : ""}`} />
                                    {errors.transactionRef && <span className="form-error">{errors.transactionRef}</span>}
                                </div>
                            )}

                        </div>

                        {/* Remarks */}
                        <div className="form-group" style={{ marginTop: "0.75rem" }}>
                            <label className="form-label">Remarks</label>
                            <textarea name="remarks" value={form.remarks} onChange={handleChange}
                                rows={2} placeholder="Optional notes about this payment (e.g. Paid in full, Cheque clearance pending, etc.)"
                                className="form-control" style={{ resize: "vertical" }} />
                        </div>
                    </div>
                </div>

                {/* ── Submit ────────────────────────────────────────────────────── */}
                <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                    <button type="button" className="btn btn-outline"
                        onClick={() => navigate(studentDetailPath)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={loading} style={{ minWidth: 160 }}>
                        {loading ? "Saving…" : isEdit ? "Update Receipt" : `Collect ${inr(grandTotal)}`}
                    </button>
                </div>

            </form>
        </div>
    );
}